import * as state from './state.js';
import { updatePreviewStatus } from './preview-status.js';
import { clearBoardLoading, failPreview, isPreviewLoading } from './preview.js';

// A socket can die without closing (a proxy drops it while idle, the laptop
// sleeps): it still reads OPEN, send() succeeds, and no reply ever comes. The
// server answers one render at a time, so silence this long while a reply is
// owed means the socket is dead, not busy.
export const RENDER_REPLY_TIMEOUT_MS = 15000;
let repliesOwed = 0;
let replyWatchdog = null;
let rerender = null;
// The socket the watchdog gave up on. Its re-render already went over HTTP, so
// neither its onclose nor the replacement's onopen touches the preview: a board
// slower than the timeout would otherwise abandon every fresh socket in turn.
let abandonedWs = null;

function rearmReplyWatchdog() {
    clearTimeout(replyWatchdog);
    replyWatchdog = repliesOwed > 0
        ? setTimeout(abandonSocket, RENDER_REPLY_TIMEOUT_MS)
        : null;
}

// close() leaves the socket CLOSING, so the re-render goes over HTTP; onclose
// then reconnects as it does for any drop.
function abandonSocket() {
    repliesOwed = 0;
    replyWatchdog = null;
    abandonedWs = state.ws;
    abandonedWs.close();
    rerender({ keepBoard: state.renderKeepsBoard });
}

export function sendRender(msg) {
    state.ws.send(JSON.stringify(msg));
    repliesOwed += 1;
    if (repliesOwed === 1) rearmReplyWatchdog();
}

export function connectWebSocket(sendYAMLToPreviewFn) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}${state.withProject('/ws')}`;

    const ws = new WebSocket(wsUrl);
    state.setWs(ws);
    rerender = sendYAMLToPreviewFn;
    repliesOwed = 0;
    rearmReplyWatchdog();

    ws.onopen = () => {
        console.log('WebSocket connected');
        updatePreviewStatus('Connected', 'connected');
        if (abandonedWs) abandonedWs = null;
        else sendYAMLToPreviewFn();
    };

    ws.onmessage = (event) => {
        repliesOwed -= 1;
        rearmReplyWatchdog();
        try {
            const data = JSON.parse(event.data);

            if (data.type === 'render') {
                const format = data.format || 'svg';
                const content = data[format] || data.svg || data.html || '';
                state.updatePreview(content, data.errors, format, data.warnings);
            } else if (data.type === 'error') {
                state.updatePreview('', [data.message]);
            }
        } catch (error) {
            // A throw escapes onmessage before updatePreview() clears the spinner,
            // so the pane spins on a render that already completed. Surface it,
            // then rethrow — this is a bug in the preview path, not a condition to
            // absorb, and the console trace is the only thing that locates it.
            // Painted unconditionally: renders overlap (500ms editor debounce vs a
            // multi-second round trip), and whatever board is on screen when the
            // newest one fails is stale by definition.
            failPreview(`Preview failed: ${error.message}`);
            updatePreviewStatus('Error', 'error');
            throw error;
        }
    };

    ws.onclose = () => {
        console.log('WebSocket disconnected');
        updatePreviewStatus('Disconnected', 'disconnected');
        repliesOwed = 0;
        rearmReplyWatchdog();
        // The board on screen stays; charts spinning for a reply won't get one.
        if (ws !== abandonedWs) {
            if (isPreviewLoading()) failPreview('Connection lost — reconnecting...');
            else clearBoardLoading();
        }
        state.setReconnectTimeout(setTimeout(() => connectWebSocket(sendYAMLToPreviewFn), 2000));
    };

    ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        updatePreviewStatus('Error', 'error');
    };
}
